<template>
  <div class="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
    <div class="grid grid-cols-1 gap-8 md:grid-cols-[320px,1fr]">
      <!-- SIDEBAR -->
      <aside class="order-2 md:order-1 space-y-6">
        <BookingSummary :currentStep="currentStep" />

        <button
          @click="handleEditSearch"
          class="flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-200 bg-slate-100 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white"
        >
          <font-awesome-icon size="lg" icon="fa-solid fa-chevron-left" />
          Edit search
        </button>
      </aside>

      <!-- MAIN -->
      <section class="order-1 md:order-2">
        <!-- LIST MODE -->
        <div v-if="!selectedCar" class="space-y-6">
          <!-- FILTER BAR -->
          <div class="rounded-2xl border border-slate-200 bg-white">
            <div class="rounded-t-2xl bg-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
              Filter your results
            </div>

            <div class="grid grid-cols-1 gap-4 border-t border-slate-200 p-5 md:grid-cols-2">
              <div>
                <div class="text-[12px] font-medium text-slate-500 uppercase">Passengers</div>
                <div class="mt-2">
                  <Dropdown name="passengers" :data="passengers" @item="handleDropItemSelected" />
                </div>
              </div>
              <div>
                <div class="text-[12px] font-medium text-slate-500 uppercase">Suitcases</div>
                <div class="mt-2">
                  <Dropdown name="suitcases" :data="suitcases" @item="handleDropItemSelected" />
                </div>
              </div>
            </div>
          </div>

          <!-- LOADER -->
          <Loader :show="isLoading" />

          <!-- RESULTS -->
          <div v-if="!isLoading && filteredCars?.length" class="space-y-5">
            <CarResult
              v-for="(car, index) in filteredCars"
              :key="index"
              isLead="1"
              :car="car"
              :canSelect="true"
              :selectedCar="selectedCar"
              @selected="handleCarSelected"
            />
          </div>

          <!-- EMPTY STATE -->
          <div
            v-else-if="!isLoading"
            class="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-600"
          >
            No cars match your filters. Try widening your selection.
          </div>
        </div>

        <!-- SELECTED CAR + CONTACT FORM -->
        <div v-else class="space-y-6">
          <div class="rounded-2xl border border-slate-200 bg-white">
            <div class="rounded-t-2xl bg-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
              Your car
            </div>
            <div class="p-5">
              <CarResult isLead="1" :car="selectedCar" :canSelect="false" :selectedCar="selectedCar" />
            </div>
          </div>

          <div class="rounded-2xl border border-slate-200 bg-white">
            <div class="rounded-t-2xl bg-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
              Guest contact details
            </div>

            <div class="grid grid-cols-1 gap-0 border-t border-slate-200 md:grid-cols-2">
              <!-- First/Last -->
              <div class="flex flex-col gap-2 p-5">
                <label :class="!errors.firstName ? 'text-slate-500' : 'text-red-600'" class="text-[12px] uppercase font-medium">
                  First name *
                  <span v-if="errors.firstName" class="text-red-600">(required)</span>
                </label>
                <input v-model="contactDetails.firstName" type="text" class="h-10 w-full border-0 bg-transparent text-base font-semibold outline-none" />
              </div>
              <div class="flex flex-col gap-2 border-t border-slate-200 p-5 md:border-l md:border-t-0">
                <label :class="!errors.lastName ? 'text-slate-500' : 'text-red-600'" class="text-[12px] uppercase font-medium">
                  Last name *
                  <span v-if="errors.lastName" class="text-red-600">(required)</span>
                </label>
                <input v-model="contactDetails.lastName" type="text" class="h-10 w-full border-0 bg-transparent text-base font-semibold outline-none" />
              </div>

              <!-- Email/Phone -->
              <div class="flex flex-col gap-2 border-t border-slate-200 p-5">
                <label :class="!errors.email ? 'text-slate-500' : 'text-red-600'" class="text-[12px] uppercase font-medium">
                  Email *
                  <span v-if="errors.email" class="text-red-600">({{ errors.email }})</span>
                </label>
                <input v-model="contactDetails.email" type="text" class="h-10 w-full border-0 bg-transparent text-base font-semibold outline-none" />
              </div>
              <div class="flex flex-col gap-2 border-t border-slate-200 p-5 md:border-l">
                <label :class="!errors.phoneNumber ? 'text-slate-500' : 'text-red-600'" class="text-[12px] uppercase font-medium">
                  Phone number *
                  <span v-if="errors.phoneNumber" class="text-red-600">({{ errors.phoneNumber }})</span>
                </label>
                <CountrySearchablePhoneField @phoneNumber="handlePhoneChange" />
              </div>

              <!-- Flight/Comments -->
              <div class="flex flex-col gap-2 border-t border-slate-200 p-5">
                <label class="text-[12px] uppercase font-medium text-slate-500">Flight number (optional)</label>
                <input v-model="contactDetails.flightNumber" type="text" class="h-10 w-full border-0 bg-transparent text-base font-semibold outline-none" />
              </div>
              <div class="flex flex-col gap-2 border-t border-slate-200 p-5 md:border-l">
                <label class="text-[12px] uppercase font-medium text-slate-500">No. of Pax , No of luggage , Child Seats, Special Requests </label>
                <textarea v-model="contactDetails.comments" class="h-[60px] w-full resize-none border-0 bg-transparent text-base font-semibold outline-none"></textarea>
              </div>
            </div>
          </div>

          <button
            @click="handleBookingProceed"
            class="group ml-auto flex w-full items-center justify-center gap-3 rounded-full border-2 border-slate-900 bg-slate-900 px-8 py-3 text-sm font-semibold text-white transition hover:bg-white hover:text-slate-900 md:w-auto"
          >
            <span>Request a quote</span>
            <font-awesome-icon class="transition group-hover:translate-x-0.5" size="lg" icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { disableScroll, enableScroll, handleApiError, isValidEmail, resetErrors, saveBookingNumber } from '@/utils/common'
import { useRouter } from 'vue-router'
import Car from '@/composables/Car'
import { BookingStep, Car as CarType, UserSearch } from '@/types'
import CarResult from '@/components/CarResult.vue'
import { BOOKING_STEPS, LOADER_MINIMUM_TIME, PASSENGERS, SUITCASES } from '@/utils/constants'
import { retrieveUserSearch, saveUserTempSearch } from '@/services/search-service'
import BookingSummary from '@/components/BookingSummary.vue'
import Loader from '@/widgets/Loader.vue'
import Dropdown from '@/components/Dropdown.vue'
import CountrySearchablePhoneField from '@/components/CountrySearchablePhoneField.vue'
import BookingService from '@/services/booking-service'
import CarService from '@/services/car-service'

const { createBooking, updateBookingAsLead } = BookingService()
const { allCars }  = Car()
const router = useRouter()
const cars = ref<CarType[]>()
const selectedCar = ref<CarType>()
const showContactForm = ref<boolean>(false)
const passengers = PASSENGERS
const isLoading = ref<boolean>(false)
const suitcases = SUITCASES
const { saveSelectedCar }  = CarService()
const filters = ref<any>({
  passengers: null,
  doors: null,
  suitcases: null,
  transmission: null
})
const contactDetails = ref<any>({
  firstName: '',
  phoneNumber: '',
  flightNumber: '',
  lastName: '',
  email: '',
  comments: '',
  needConfirmation: false,
})
const errors = ref<any>({
  firstName: null,
  lastName: null,
  email: null,
  phoneNumber: null,
  responseError: null
})
const timeElapsed = ref<number>(0)
const steps = ref<BookingStep[]>(BOOKING_STEPS)
const currentStep = steps.value.filter((step) => step.uri === 'choose-vehicle')
const userSearch = ref<UserSearch>(retrieveUserSearch())

const handleCarSelected = (car: CarType) => {
  selectedCar.value = car
  // force visible price = 0 in the “lead” flow (kept from your original)
  // @ts-ignore
  selectedCar.value.price = 0
  showContactForm.value = true
  cars.value = [selectedCar.value as CarType]
}

/** Category rank for sorting:
 *  Premium (0) → Luxury (1) → SUV (2) → Van (3) → Others (99)
 *  Checks both car.type.name and car.name (case-insensitive).
 */
const categoryRank = (car: any): number => {
  const src = String(car?.type?.name || car?.name || '').toLowerCase()
  if (src.includes('premium')) return 0
  if (src.includes('luxury') || src.includes('lusary')) return 1
  if (src.includes('suv')) return 2
  if (src.includes('van')) return 3
  return 99
}

/** Final list: filter → sort by category → tie-break by passengers desc → then name asc */
const filteredCars = computed(() => {
  const list = (cars.value || []).filter((car: any) => {
    return (
      (!filters.value.passengers || (car?.type?.passengers ?? 0) >= filters.value.passengers) &&
      (!filters.value.doors || (car?.type?.doors ?? null) === filters.value.doors) &&
      (!filters.value.suitcases || (car?.type?.bags ?? 0) >= filters.value.suitcases) &&
      (!filters.value.transmission || (car?.transmission ?? '') === filters.value.transmission)
    )
  })

  return list.slice().sort((a: any, b: any) => {
    const ra = categoryRank(a)
    const rb = categoryRank(b)
    if (ra !== rb) return ra - rb

    // tie-break 1: more passengers first
    const paxA = Number(a?.type?.passengers ?? 0)
    const paxB = Number(b?.type?.passengers ?? 0)
    if (paxA !== paxB) return paxB - paxA

    // tie-break 2: name asc
    const na = String(a?.name || a?.type?.name || '')
    const nb = String(b?.name || b?.type?.name || '')
    return na.localeCompare(nb)
  })
})

const handleDropItemSelected = (item: any, name: string) => {
  const val = item?.value?.value === '' ? null : item?.value?.value
  if (name === 'passengers') filters.value.passengers = val
  else if (name === 'suitcases') filters.value.suitcases = val
  else if (name === 'doors') filters.value.doors = val
  else if (name === 'transmission') filters.value.transmission = val
}

const handleEditSearch = () => {
  saveUserTempSearch(userSearch.value)
  router.push({name: 'Home'})
}

const handlePhoneChange = (value: string) => {
  contactDetails.value.phoneNumber = value
}

const handleBookingProceed = async () => {
  resetErrors(errors.value)
  let isValid = validateForm()

  if (!isLoading.value && isValid) {
    if (!selectedCar.value) {
      console.log('Error in data collection')
      return
    }
    saveSelectedCar(selectedCar.value as CarType)
    isLoading.value = true
    const createResponse = await createBooking(selectedCar.value as CarType, userSearch.value as UserSearch)
    if (createResponse.success) {
      saveBookingNumber(createResponse.data.data.number)
      const updateResponse = await updateBookingAsLead(createResponse.data.data.number, contactDetails.value)
      if (updateResponse.success) {
        router.push({ name: 'BookingWizard', params: { step: 'finish' } })
      }
    }
  }
}

const validateForm = () => {
  let response = true

  if (contactDetails.value.firstName === '') {
    errors.value.firstName = true
    response = false
  }
  if (contactDetails.value.lastName === '') {
    errors.value.lastName = true
    response = false
  }
  if (contactDetails.value.phoneNumber === '') {
    errors.value.phoneNumber = 'Required'
    response = false
  } else if (contactDetails.value.phoneNumber.length < 11) {
    errors.value.phoneNumber = 'Invalid'
    response = false
  }
  if (contactDetails.value.email === '') {
    errors.value.email = 'Required'
    response = false
  } else if (!isValidEmail(contactDetails.value.email)) {
    errors.value.email = 'Invalid'
    response = false
  }
  return response
}

onMounted(async () => {
  isLoading.value = true
  disableScroll()
  const startTime = performance.now()
  const response = await allCars()
  timeElapsed.value = performance.now() - startTime
  if (timeElapsed.value < LOADER_MINIMUM_TIME) {
    const diff = LOADER_MINIMUM_TIME - timeElapsed.value
    setTimeout(() => {
      enableScroll()
      isLoading.value = false
    }, diff)
  } else {
    enableScroll()
    isLoading.value = false
  }

  if (response.status === 200) {
    cars.value = response.data.cars
  } else {
    handleApiError(response)
  }
})
</script>